import { ApolloProvider } from '@apollo/client';
import { DataFetchingClientProvider } from 'data-fetching-client';
import { createApolloClient } from 'hubspot-apollo-client';
import { createContext, useContext, useMemo } from 'react';
import { ViewsActionsProvider } from '../viewsActions/context/ViewsActionsModalContext';
import { getDefaultDataFetchingClient } from './getDefaultDataFetchingClient';
import { jsx as _jsx } from "react/jsx-runtime";
const ClientsContext = /*#__PURE__*/createContext(null);
const ExternalViewStateContext = /*#__PURE__*/createContext({
  currentViewDraft: null,
  supportedVisualizations: null,
  cloneViewOverride: undefined
});
export const useCurrentViewDraft = () => {
  const {
    currentViewDraft
  } = useContext(ExternalViewStateContext);
  // specifically check for null, because undefined is a valid value
  if (currentViewDraft === null) {
    throw new Error('useCurrentViewDraft must be used within a CurrentViewDraftContext');
  }
  return currentViewDraft;
};

// Hook to access viz-core mutation functions
export const useViewMutationOverride = () => {
  const {
    cloneViewOverride
  } = useContext(ExternalViewStateContext);
  return {
    cloneViewOverride
  };
};
const DEFAULT_SUPPORTED_VISUALIZATIONS = new Set(['list', 'board']);
export const useSupportedVisualizations = () => {
  const {
    supportedVisualizations
  } = useContext(ExternalViewStateContext);
  if (supportedVisualizations === null) {
    throw new Error('useSupportedVisualizations must be used within a SupportedVisualizationsContext');
  }
  return supportedVisualizations;
};

/**
 * Provides DataFetchingClient and ApolloClient Contexts
 * The default DataFetchingClient is generated with view-specfic queryFieldPolicies
 *
 * If rendered inside of another ViewsManagementProvider, the clients provided
 * by the outer context will be used
 *
 * This means that nesting two ViewsManagementProviders will result in a shared cache
 * (assuming prop overrides aren't used)
 *
 * Apps can also provide their own DataFetchingClient and ApolloClient instances
 * if they have merged the query and mutation typePolicies in manually
 */
export const ViewsManagementProvider = ({
  children,
  dfcClient,
  apolloClient,
  currentViewDraft,
  supportedVisualizations,
  cloneViewOverride
}) => {
  const {
    client: dfcClientFromContext,
    apolloClient: apolloClientFromContext
  } = useContext(ClientsContext) || {};
  const resolvedClient = useMemo(() => dfcClient || dfcClientFromContext || getDefaultDataFetchingClient(), [dfcClient, dfcClientFromContext]);
  const resolvedApolloClient = useMemo(() => apolloClient || apolloClientFromContext || createApolloClient(), [apolloClient, apolloClientFromContext]);
  const resolvedSupportedVisualizations = useMemo(() => supportedVisualizations !== null && supportedVisualizations !== void 0 && supportedVisualizations.size ? supportedVisualizations : DEFAULT_SUPPORTED_VISUALIZATIONS, [supportedVisualizations]);
  return /*#__PURE__*/_jsx(ClientsContext.Provider, {
    value: {
      client: resolvedClient,
      apolloClient: resolvedApolloClient
    },
    children: /*#__PURE__*/_jsx(ApolloProvider, {
      client: resolvedApolloClient,
      children: /*#__PURE__*/_jsx(DataFetchingClientProvider, {
        client: resolvedClient,
        children: /*#__PURE__*/_jsx(ViewsActionsProvider, {
          children: /*#__PURE__*/_jsx(ExternalViewStateContext.Provider, {
            value: {
              currentViewDraft,
              supportedVisualizations: resolvedSupportedVisualizations,
              cloneViewOverride
            },
            children: children
          })
        })
      })
    })
  });
};